import { captureException } from '@sentry/react';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import {
    NotePinConfirmationDialog,
    type NotePinConfirmationDialogProps,
} from '../components/note-pin-confirmation-dialog.tsx';

type UsePinNoteOptions = {
    onPinned?: () => void;
};

export const usePinNote = (note: CrmNoteDTO, { onPinned }: UsePinNoteOptions = {}) => {
    const { t } = useLocale('notes');
    const { toast } = useToast();
    const sendEvent = useAnalytics();
    const apiUtils = api.useUtils();
    const { data } = api.platoUser.query.customerActivity.getFeedList.getPinnedNotes.useQuery(
        {
            customerId: note.customerId,
        },
        {
            refetchOnMount: false,
            enabled: !!note.customerId && !note.pinnedAt,
        },
    );

    const hasAnotherPinnedNote = !!data?.length;

    const { mutate, ...pinMutation } = api.platoUser.command.notes.pin.useMutation({
        onSuccess: () => {
            toast({
                icon: 'success',
                title: t('notes__pin_success_toast'),
            });

            sendEvent('customer_note_pinned', {
                note_id: note.noteId,
                customer_id: note.customerId ?? 'unknown',
                note_type: note.noteType,
            });

            // Invalidate both pinned notes and activity feed queries
            apiUtils.platoUser.query.customerActivity.getFeedList.getPinnedNotes.invalidate();
            apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
            apiUtils.platoUser.query.notes.getNoteById.invalidate({ noteId: note.noteId });

            onPinned?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const applyMutation = () => {
        if (!note.customerId) {
            captureException(new Error(`Customer ID is missing for note ${note.noteId}`));
            return;
        }

        mutate({ noteId: note.noteId, customerId: note.customerId });
    };

    const pin = () => {
        if (hasAnotherPinnedNote) {
            DialogStateManager.show(NotePinConfirmationDialog, {
                note,
                onConfirm: applyMutation,
            } satisfies NotePinConfirmationDialogProps);
        } else {
            applyMutation();
        }
    };

    return {
        ...pinMutation,
        pin,
    };
};
